import { useEffect, useMemo } from 'react';

import { useFieldArray, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { parseFormNumber, roundToPrecision } from '@/helpers/finance';
import { getPaymentTermsOptions } from '@/modules/clients/constants/payment-terms';
import { useCurrencies } from '@/modules/common/hooks';
import { useFeatureFlags } from '@/modules/common/hooks/use-feature-flags';
import { FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES } from '@/modules/finance/common/constants/rounding.constants';
import { usePaymentDetailsShort } from '@/modules/settings/hooks/payment-details/use-payment-details-short';
import { parseCalendarDate } from '@/utils/date-formatters';

import { InvoiceDetails, InvoiceEntityType } from '../../types';
import { calculateInvoiceDueDate } from '../../utils/calculate-invoice-due-date';
import {
  getUpdateInvoiceItemValidationIssues,
  getUpdateInvoiceValidationIssues,
  InvoiceValidationIssueCode,
  UPDATE_INVOICE_MESSAGE_BY_ISSUE,
  UpdateInvoiceFormValues,
} from '../../validations';
import { InvoiceGeneralInformationCard } from './invoice-general-information-card';
import { InvoiceLineItemsCard } from './invoice-line-items-card';
import { InvoicePaymentDetailsCard } from './invoice-payment-details-card';
import { InvoiceReceiversCard } from './invoice-receivers-card';
import { InvoiceTotalsCard } from './invoice-totals-card';

interface InvoiceDetailsFormProps {
  isReadOnly: boolean;
  details: InvoiceDetails;
  form: UseFormReturn<UpdateInvoiceFormValues>;
}

const TOTAL_ERROR_MESSAGE =
  UPDATE_INVOICE_MESSAGE_BY_ISSUE[InvoiceValidationIssueCode.NegativeTotal];
const EMPTY_LINE_ITEMS_ERROR_MESSAGE =
  UPDATE_INVOICE_MESSAGE_BY_ISSUE[InvoiceValidationIssueCode.EmptyItems];

/** Renders the shared invoice details layout in either editable or read-only mode. */
export function InvoiceDetailsForm({ isReadOnly, details, form }: InvoiceDetailsFormProps) {
  const { t } = useTranslation(['authenticated.invoices', 'authenticated.clients', 'common']);
  const noValue = t('common:table.noValue');
  const { currencies, isLoadingCurrencies } = useCurrencies();
  const { showPaymentDetails } = useFeatureFlags();
  const { data: paymentDetailsEntries = [], isLoading: isLoadingPaymentDetails } =
    usePaymentDetailsShort({ enabled: showPaymentDetails && !isReadOnly });
  const { fields, append, insert, remove } = useFieldArray({
    control: form.control,
    name: 'items',
  });
  const currencyCode = useWatch({ control: form.control, name: 'currencyCode' });
  const paymentDetailsId = useWatch({ control: form.control, name: 'paymentDetailsId' });
  const period = useWatch({ control: form.control, name: 'period' });
  const issuedDate = useWatch({ control: form.control, name: 'issuedDate' });
  const paymentTerm = useWatch({ control: form.control, name: 'paymentTerm' });
  const items = useWatch({ control: form.control, name: 'items' });
  const paymentTermOptions = useMemo(() => getPaymentTermsOptions(t), [t]);
  const selectedPaymentMemo =
    paymentDetailsEntries.find(entry => entry.id === paymentDetailsId)?.details ??
    (paymentDetailsId === details.paymentDetailsId ? details.paymentMemo : null) ??
    '';
  const paymentMemo = isReadOnly ? (details.paymentMemo ?? '') : selectedPaymentMemo;
  const dueDate = calculateInvoiceDueDate({ issuedDate, paymentTerm });
  const initialPeriod = form.formState.defaultValues?.period;
  const hasBillingPeriodChanged =
    period?.from?.getTime() !== initialPeriod?.from?.getTime() ||
    period?.to?.getTime() !== initialPeriod?.to?.getTime();
  const editableTotalAmount = roundToPrecision(
    items.reduce((total, item) => {
      const amount =
        item.entityType === InvoiceEntityType.User || item.entityType === InvoiceEntityType.Overhead
          ? parseFormNumber(item.quantity) * parseFormNumber(item.unitPrice)
          : parseFormNumber(item.amount);
      return total + amount;
    }, 0),
    FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES
  ).toFixed(FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES);
  const editableBalance = roundToPrecision(
    parseFormNumber(editableTotalAmount) - parseFormNumber(details.amountPaid),
    FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES
  ).toFixed(FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES);
  const lineValidationIssues = useMemo(
    () => (isReadOnly ? [] : items.map(getUpdateInvoiceItemValidationIssues)),
    [isReadOnly, items]
  );
  const invoiceValidationIssues = useMemo(
    () => (isReadOnly ? [] : getUpdateInvoiceValidationIssues(items)),
    [isReadOnly, items]
  );
  const isTotalError = invoiceValidationIssues.includes(InvoiceValidationIssueCode.NegativeTotal);
  const isEmptyLineItemsError = invoiceValidationIssues.includes(
    InvoiceValidationIssueCode.EmptyItems
  );
  const addUserItem = () => {
    const firstOverheadIndex = fields.findIndex(
      item => item.entityType === InvoiceEntityType.Overhead
    );
    const insertionIndex = firstOverheadIndex === -1 ? fields.length : firstOverheadIndex;

    insert(insertionIndex, {
      entityType: InvoiceEntityType.User,
      entityId: '',
      description: '',
      quantity: '0',
      unitPrice: '0',
      amount: '0',
    });
  };
  const addOverheadItem = () =>
    append({
      entityType: InvoiceEntityType.Overhead,
      entityId: '',
      description: '',
      quantity: '1',
      unitPrice: '',
      amount: '0',
    });

  useEffect(() => {
    if (isReadOnly || !hasBillingPeriodChanged || !period?.to) return;

    form.setValue('issuedDate', parseCalendarDate(period.to), {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [form, hasBillingPeriodChanged, isReadOnly, period?.to]);

  useEffect(() => {
    if (isReadOnly) return;

    // Item field changes validate only that field; revalidate the array for total and balance rules.
    void form.trigger('items');
  }, [form, isReadOnly, items]);

  return (
    <div className="grid w-full max-w-7xl grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
      <div className="order-last flex flex-col gap-4 lg:sticky lg:top-0 lg:order-last">
        <InvoiceTotalsCard
          details={details}
          isReadOnly={isReadOnly}
          currencyCode={currencyCode}
          totalAmount={editableTotalAmount}
          balance={editableBalance}
          totalErrorMessage={!isReadOnly && isTotalError ? TOTAL_ERROR_MESSAGE : undefined}
        />
      </div>

      <div className="flex min-w-0 flex-col gap-6">
        <InvoiceGeneralInformationCard
          details={details}
          form={form}
          isReadOnly={isReadOnly}
          noValue={noValue}
          dueDate={dueDate}
          currencyOptions={currencies.map(currency => ({
            value: currency.code,
            label: currency.toString(),
          }))}
          isLoadingCurrencies={isLoadingCurrencies}
          paymentTermOptions={paymentTermOptions.map(option => ({
            value: option.value,
            label: option.label,
          }))}
        />
        <InvoiceReceiversCard form={form} isReadOnly={isReadOnly} />
        <InvoiceLineItemsCard
          details={details}
          form={form}
          isReadOnly={isReadOnly}
          noValue={noValue}
          currencyCode={currencyCode}
          fields={fields}
          lineValidationIssues={lineValidationIssues}
          isEmptyLineItemsError={isEmptyLineItemsError}
          emptyLineItemsErrorMessage={EMPTY_LINE_ITEMS_ERROR_MESSAGE}
          onRemove={remove}
          onAddUserItem={addUserItem}
          onAddOverheadItem={addOverheadItem}
        />

        {showPaymentDetails && (
          <InvoicePaymentDetailsCard
            form={form}
            isReadOnly={isReadOnly}
            paymentMemo={paymentMemo}
            noValue={noValue}
            entries={paymentDetailsEntries}
            isLoading={isLoadingPaymentDetails}
          />
        )}
      </div>
    </div>
  );
}
